import { Checkbox, FormControlLabel, Grid, TextField, Autocomplete } from '@mui/material';

import { useFormikContext, Field } from 'formik';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';
import { countries, CountryType, countryToFlag } from 'utils/Countries';

import ErrorMarker from '../components/ErrorMarker';

export default function CompanyData() {
  const { errors, touched, values, setFieldValue, validateForm }: any = useFormikContext<any>();
  const { t } = useTranslation();

  return (
    <>
      <Grid container className="register-form__description">
        {t(translations.pages.login.formDescriptions.companyData())}
      </Grid>
      <Grid container>
        <Grid item xs>
          <Field
            className="register-form__field"
            label={t(translations.common.companyName())}
            name="companyData.name"
            as={TextField}
            error={touched.companyData?.name && errors.companyData?.name}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.companyData?.name && errors.companyData?.name && (
          <ErrorMarker message={errors.companyData?.name} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs>
          <Field
            className="register-form__field"
            label={t(translations.common.street())}
            name="companyData.street"
            as={TextField}
            error={touched.companyData?.street && errors.companyData?.street}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.companyData?.street && errors.companyData?.street && (
          <ErrorMarker message={errors.companyData?.street} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.zip())}
            name="companyData.zip"
            as={TextField}
            error={touched.companyData?.zip && errors.companyData?.zip}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.companyData?.zip && errors.companyData?.zip && (
          <ErrorMarker message={errors.companyData?.zip} />
        )}
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.city())}
            name="companyData.city"
            as={TextField}
            error={touched.companyData?.city && errors.companyData?.city}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.companyData?.city && errors.companyData?.city && (
          <ErrorMarker message={errors.companyData?.city} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <FormControlLabel
            control={
              <Autocomplete
                className="register-form__field"
                options={countries as CountryType[]}
                autoHighlight
                fullWidth
                value={
                  countries.find(c => c.label === values.companyData.country)
                    ? countries.find(c => c.label === values.companyData.country)
                    : null
                }
                getOptionLabel={option => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span style={{ marginRight: '0.5em' }}>{countryToFlag(option.code)}</span>
                    {option.label} ({option.code})
                  </li>
                )}
                onChange={async (event, value, reason) => {
                  await Promise.all([
                    setFieldValue('companyData.country', value?.label, false),
                    setFieldValue('invoiceData.taxId', '', false),
                    setFieldValue('invoiceData.taxNumber', '', false)
                  ]);
                  await validateForm();
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t(translations.common.country()) + '*'}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password'
                    }}
                    size="small"
                  />
                )}
              />
            }
            label={''}
            style={{ width: '100%' }}
          />
        </Grid>
        {touched.companyData?.country && errors.companyData?.country && (
          <ErrorMarker message={errors.companyData?.country} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.useSameAddress}
                onChange={e => {
                  setFieldValue('useSameAddress', e.target.checked);
                  if (!e.target.checked) {
                    setFieldValue('invoiceData', {});
                  }
                }}
              />
            }
            label={t(translations.common.useSameAddress()) as string}
          />
        </Grid>
        {touched.companyData?.useSameAddress && errors.companyData?.useSameAddress && (
          <ErrorMarker message={errors.companyData?.useSameAddress} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.phone())}
            name="companyData.phone"
            as={TextField}
            error={touched.companyData?.phone && errors.companyData?.phone}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Grid>
        {touched.companyData?.phone && errors.companyData?.phone && (
          <ErrorMarker message={errors.companyData?.phone} />
        )}
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.fax())}
            name="companyData.fax"
            as={TextField}
            error={touched.companyData?.fax && errors.companyData?.fax}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Grid>
        {touched.companyData?.fax && errors.companyData?.fax && (
          <ErrorMarker message={errors.companyData?.fax} />
        )}
      </Grid>
      <Grid container>
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.homepage())}
            name="companyData.homepage"
            as={TextField}
            error={touched.companyData?.homepage && errors.companyData?.homepage}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        {touched.companyData?.homepage && errors.companyData?.homepage && (
          <ErrorMarker message={errors.companyData?.homepage} />
        )}
        <Grid item xs={11} md>
          <Field
            className="register-form__field"
            label={t(translations.common.email_short())}
            name="companyData.email"
            as={TextField}
            error={touched.companyData?.email && errors.companyData?.email}
            variant="outlined"
            size="small"
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.agreedToNewsletter}
                onChange={e => {
                  setFieldValue('agreedToNewsletter', e.target.checked);
                }}
              />
            }
            label={t(translations.pages.manageCompanies.agreeToNewsletter())}
          />
        </Grid>
        {touched.companyData?.email && errors.companyData?.email && (
          <ErrorMarker message={errors.companyData?.email} />
        )}
      </Grid>
      <Grid container className="register-form__info">
        * {t(translations.common.requiredFields())}
      </Grid>
    </>
  );
}
